/**
 * Widget embebible — entry IIFE.
 *
 * Uso desde HTML host:
 *   <div id="payment"></div>
 *   <script src="https://wallet.intranet/widget.js"></script>
 *   <script>
 *     WalletPay.mount({
 *       target: '#payment',
 *       apiUrl: 'https://wallet.intranet/api',   // opcional, default: misma origin del script
 *       email: 'cliente@ejemplo.com',            // opcional
 *       walletAddress: '0x...',                  // opcional
 *       onSuccess: (result) => console.log(result),
 *     });
 *   </script>
 *
 * Auto-mount: si hay un <div data-walletpay-mount> en la página al cargar el script,
 * el widget se monta automáticamente leyendo data-* attributes.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { PaymentForm } from './widget/PaymentForm.js';
import { BiometricVerification } from './widget/BiometricVerification.js';
import { WIDGET_CSS } from './widget/styles.js';
import type { CaptureResult, Chain } from './widget/api.js';
import type { WidgetApiError } from './widget/api.js';

export type WidgetTheme = 'light' | 'dark' | 'auto';

const isChain = (v: unknown): v is Chain =>
  v === 'ethereum' || v === 'bitcoin' || v === 'lightning';

export interface MountOptions {
  /** Selector CSS o HTMLElement donde montar el widget. */
  target: string | HTMLElement;
  /** Base URL de la API. Default: misma origin que el script. */
  apiUrl?: string;
  /**
   * Username único. Si el host lo pasa, pre-rellena el input.
   * Lpay provisiona el mailbox en kmail con este username y la address
   * resultante ES el email del user. El widget no genera emails.
   */
  username?: string;
  /**
   * Cadena de la wallet de payout. 'ethereum' (default), 'bitcoin' o 'lightning'.
   * Cuando es 'lightning', `walletAddress` es obligatorio (LN no se auto-genera).
   */
  chain?: Chain;
  walletAddress?: string;
  /**
   * Tema visual.
   *  - "light": siempre claro (fuerza light incluso con OS en dark)
   *  - "dark":  siempre oscuro
   *  - "auto":  sigue prefers-color-scheme del OS (default)
   */
  theme?: WidgetTheme;
  onSuccess?: (result: CaptureResult) => void;
  onError?: (error: WidgetApiError) => void;
  /**
   * MODO VERIFICACIÓN BIOMÉTRICA.
   *
   * Si se pasa `paymentId`, el widget NO muestra el formulario de tarjeta:
   * muestra la verificación de identidad guiada, conectada en tiempo real al
   * payments-api (`scrapperApiUrl`). El host activa este modo una vez que tiene
   * el `payment_id` del scrapper (tras crear el payment). `scrapperApiUrl` es
   * obligatorio en este modo.
   */
  paymentId?: string;
  /** Base URL del payments-api (scrapper). Requerido junto con `paymentId`. */
  scrapperApiUrl?: string;
  /** Modo biométrico: se dispara cuando el KYC queda verificado. */
  onVerified?: () => void;
  /** Modo biométrico: se dispara si la verificación/payment termina en fallo. */
  onFailed?: (info: { status: string; code: string | null; message: string | null }) => void;
}

const isWidgetTheme = (v: unknown): v is WidgetTheme =>
  v === 'light' || v === 'dark' || v === 'auto';

export interface WidgetInstance {
  /** Desmonta el widget y limpia el shadow root. */
  unmount: () => void;
}

// ---------------------------------------------------------------------------
// Resolver la URL del script para inferir apiUrl por defecto
// ---------------------------------------------------------------------------

const inferApiBase = (): string => {
  if (typeof document === 'undefined') return '/api';
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
  const self = scripts.find((s) => /\/widget\.js(\?|$)/.test(s.src));
  if (!self) return '/api';
  try {
    const u = new URL(self.src);
    return `${u.origin}/api`;
  } catch {
    return '/api';
  }
};

const resolveTarget = (target: string | HTMLElement): HTMLElement => {
  if (typeof target === 'string') {
    const el = document.querySelector(target);
    if (!el) throw new Error(`WalletPay.mount: target "${target}" no encontrado.`);
    if (!(el instanceof HTMLElement))
      throw new Error(`WalletPay.mount: target "${target}" no es HTMLElement.`);
    return el;
  }
  return target;
};

// ---------------------------------------------------------------------------
// API pública — montar
// ---------------------------------------------------------------------------

export const mount = (opts: MountOptions): WidgetInstance => {
  const host = resolveTarget(opts.target);
  const apiBase = opts.apiUrl ?? inferApiBase();

  // Tema: el CSS dentro del shadow root usa :host([data-walletpay-theme="..."])
  const theme: WidgetTheme = opts.theme ?? 'auto';
  host.setAttribute('data-walletpay-theme', theme);

  // Reutilizar shadow root si ya existe (re-mount tras unmount, ej. al cambiar tema).
  // attachShadow falla si ya hay uno; tampoco se puede "desadjuntar" un shadow.
  let shadow: ShadowRoot;
  if (host.shadowRoot) {
    shadow = host.shadowRoot;
    while (shadow.firstChild) shadow.removeChild(shadow.firstChild);
  } else {
    host.innerHTML = ''; // limpia lightDOM solo en el primer mount
    shadow = host.attachShadow({ mode: 'open' });
  }

  // Inyectar estilos
  const style = document.createElement('style');
  style.textContent = WIDGET_CSS;
  shadow.appendChild(style);

  // Container donde se monta React
  const mountPoint = document.createElement('div');
  shadow.appendChild(mountPoint);

  // Modo biométrico: si el host pasó `paymentId`, mostramos la verificación de
  // identidad en vez del formulario de tarjeta. Requiere `scrapperApiUrl`.
  if (opts.paymentId && !opts.scrapperApiUrl) {
    throw new Error(
      'WalletPay.mount: `scrapperApiUrl` es obligatorio cuando se pasa `paymentId`.',
    );
  }

  const content =
    opts.paymentId && opts.scrapperApiUrl ? (
      <BiometricVerification
        scrapperApiUrl={opts.scrapperApiUrl}
        paymentId={opts.paymentId}
        {...(opts.onVerified ? { onVerified: opts.onVerified } : {})}
        {...(opts.onFailed ? { onFailed: opts.onFailed } : {})}
      />
    ) : (
      <PaymentForm
        apiBase={apiBase}
        {...(opts.username ? { username: opts.username } : {})}
        {...(opts.chain ? { chain: opts.chain } : {})}
        {...(opts.walletAddress ? { walletAddress: opts.walletAddress } : {})}
        {...(opts.onSuccess ? { onSuccess: opts.onSuccess } : {})}
        {...(opts.onError ? { onError: opts.onError } : {})}
      />
    );

  const root: Root = createRoot(mountPoint);
  root.render(<StrictMode>{content}</StrictMode>);

  return {
    unmount: () => {
      root.unmount();
      // Limpia el contenido del shadow (no el shadow root en sí — no se puede desadjuntar).
      while (shadow.firstChild) shadow.removeChild(shadow.firstChild);
    },
  };
};

// ---------------------------------------------------------------------------
// Auto-mount: <div data-walletpay-mount data-email="..." data-wallet="0x...">
// ---------------------------------------------------------------------------

const autoMount = (): void => {
  if (typeof document === 'undefined') return;
  const targets = document.querySelectorAll<HTMLElement>('[data-walletpay-mount]');
  targets.forEach((el) => {
    if (el.dataset.walletpayMounted === 'true') return; // idempotente
    el.dataset.walletpayMounted = 'true';
    const opts: MountOptions = {
      target: el,
      ...(el.dataset.username ? { username: el.dataset.username } : {}),
      ...(el.dataset.apiUrl ? { apiUrl: el.dataset.apiUrl } : {}),
      ...(isChain(el.dataset.chain) ? { chain: el.dataset.chain } : {}),
      ...(el.dataset.wallet ? { walletAddress: el.dataset.wallet } : {}),
      ...(isWidgetTheme(el.dataset.theme) ? { theme: el.dataset.theme } : {}),
      // Modo biométrico: <div data-walletpay-mount data-payment-id="..." data-scrapper-url="...">
      ...(el.dataset.paymentId ? { paymentId: el.dataset.paymentId } : {}),
      ...(el.dataset.scrapperUrl ? { scrapperApiUrl: el.dataset.scrapperUrl } : {}),
    };
    try {
      mount(opts);
    } catch (err) {
      console.error('[WalletPay] auto-mount falló:', err);
    }
  });
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount);
  } else {
    autoMount();
  }
}

// ---------------------------------------------------------------------------
// Exponer en window.WalletPay (también re-exportado para módulos ES)
// ---------------------------------------------------------------------------

/**
 * Atajo para el modo biométrico:
 *   WalletPay.mountBiometric({ target, scrapperApiUrl, paymentId, onVerified });
 * Equivalente a `mount` con `paymentId` presente, pero hace `scrapperApiUrl` y
 * `paymentId` obligatorios en el tipo.
 */
export interface MountBiometricOptions
  extends Omit<MountOptions, 'paymentId' | 'scrapperApiUrl'> {
  paymentId: string;
  scrapperApiUrl: string;
}

export const mountBiometric = (opts: MountBiometricOptions): WidgetInstance =>
  mount(opts);

declare global {
  interface Window {
    WalletPay?: {
      mount: typeof mount;
      mountBiometric: typeof mountBiometric;
    };
  }
}

if (typeof window !== 'undefined') {
  window.WalletPay = { mount, mountBiometric };
}
